import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  User as UserIcon,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Layers,
  Utensils,
  Target,
  Lock
} from "lucide-react";
import { useMySignedNdas } from "@/hooks/useNda";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTransferRequest } from "@/hooks/useTransferRequest";
import { useOfferTransferRequest, useMyTransferRequestOffers } from "@/hooks/useOffer";
import { useAuthStore } from "@/store/authStore";
import { can } from "@/utils/permissions";
import { useToggleSaveListing } from "@/hooks/useSavedListings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/misc";
import { Spinner, NdaSignModal, OfferModal } from "@/components/common";
import { Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TransferRequestImage } from "@/types";
import { formatPriceExact } from "@/utils";

function getImages(images?: TransferRequestImage[]) {
  if (!images?.length) return [];
  return images.map((i) => ({
    url: i.image.publicUrl,
    type: i.type,
    isPrimary: i.isPrimary,
  }));
}

const offerSchema = z.object({
  message: z.string().min(10, "Tối thiểu 10 ký tự"),
});
type OfferData = z.infer<typeof offerSchema>;

export default function TransferRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data: item, isLoading, error, refetch } = useTransferRequest(id!);
  const isNdaError = error && (error as any).response?.data?.message === "Please Sign NDA";

  const { mutate: createOffer, isPending } = useOfferTransferRequest();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = item ? isSaved(item.id) : false;
  const { data: myOffers } = useMyTransferRequestOffers(user?.id);
  const isOffered = Array.isArray(myOffers) ? myOffers.some((o: any) => o.entityId === item?.id) : false;

  const { data: mySignedNdas } = useMySignedNdas();
  const isSignedNda = Array.isArray(mySignedNdas) ? mySignedNdas.some((n: any) => n.entityId === item?.id && n.entityType === 'BusinessTransferRequest') : false;
  const ndaRequired = item?.ndaRequired === true;
  const needsToSignNda = ndaRequired && !isSignedNda;



  const [imgIdx, setImgIdx] = useState(0);
  const [offerOpen, setOfferOpen] = useState(false);
  const [ndaOpen, setNdaOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OfferData>({
    resolver: zodResolver(offerSchema),
  });

  if (isLoading) return <Spinner />;

  if (isNdaError) {
    return (
      <div className="max-w-3xl mx-auto py-20 text-center animate-fade-in">
        <div className="bg-white rounded-2xl shadow-sm border p-10 max-w-lg mx-auto">
          <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800">Yêu cầu bảo mật thông tin</h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Tin đăng này yêu cầu bạn phải ký Thỏa thuận bảo mật thông tin (NDA) trước khi xem chi tiết và trao đổi với người đăng.
          </p>
          <Button
            onClick={() => setNdaOpen(true)}
            className="w-full btn-brand py-3 text-base mb-3"
          >
            Ký NDA để xem chi tiết
          </Button>
          <Link to="/nhu-cau-sang-nhuong">
            <Button variant="ghost" className="w-full">
              Quay lại danh sách
            </Button>
          </Link>
        </div>

        {/* ══ NDA MODAL (For Error Screen) ══ */}
        <NdaSignModal
          isOpen={ndaOpen}
          onClose={() => setNdaOpen(false)}
          entityType="BusinessTransferRequest"
          entityId={id!}
          description="Bạn cần ký Thỏa thuận bảo mật thông tin (NDA) để xem chi tiết và trao đổi với người đăng."
          onSuccess={() => {
            setNdaOpen(false);
            refetch();
          }}
        />
      </div>
    );
  }

  if (!item)
    return (
      <div className="text-center py-20">
        <p style={{ color: "var(--text-3)" }}>Không tìm thấy nhu cầu này</p>
        <Link to="/nhu-cau-sang-nhuong">
          <Button variant="ghost" className="mt-4">
            Quay lại danh sách
          </Button>
        </Link>
      </div>
    );

  const imgs = getImages(item.images);

  const title = item.preferredCategories?.length
    ? `Cần tìm ${item.preferredCategories.join(", ")}`
    : "Nhu cầu mua sang nhượng";

  const fullAddr = [
    item.ward?.name,
    item.district?.name,
    item.province?.name,
    item.country?.name,
  ]
    .filter(Boolean)
    .join(", ");

  const onSubmitOffer = (data: OfferData) => {
    const payload = {
      entityType: "BusinessTransferRequest",
      entityId: item.id,
      receiverId: item.creator?.id || "",
      content: {
        title: title,
      },
      message: data.message,
      status: "pending",
    };
    createOffer(payload as any, {
      onSuccess: () => {
        reset();
        setOfferOpen(false);
      },
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-20">
      <div className="flex items-center justify-between gap-4">
        <div
          className="flex items-center gap-2 text-sm"
          style={{ color: "var(--text-3)" }}
        >
          <Link
            to="/nhu-cau-sang-nhuong"
            className="flex items-center gap-1 hover:text-orange-500 transition-colors"
          >
            <ArrowLeft size={14} /> Nhu cầu sang nhượng
          </Link>
          <span>/</span>
          <span className="truncate max-w-xs" style={{ color: "var(--text-1)" }}>
            {title}
          </span>
        </div>

        {user?.id === item.createdBy && (
          <Link to={`/nhu-cau-sang-nhuong/sua/${item.id}`}>
            <Button variant="outline" size="sm" className="gap-2 font-bold shadow-sm h-9 border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700">
              Chỉnh sửa tin đăng
            </Button>
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ── Left: Main content ── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Image gallery */}
          <Card className="overflow-hidden">
            <div className="relative h-64 md:h-80 bg-gray-100">
              {imgs.length > 0 ? (
                <>
                  <img
                    src={imgs[imgIdx].url}
                    alt={title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "";
                    }}
                  />
                  {imgs.length > 1 && (
                    <>
                      <button
                        onClick={() =>
                          setImgIdx((i) => (i - 1 + imgs.length) % imgs.length)
                        }
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        onClick={() => setImgIdx((i) => (i + 1) % imgs.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {imgs.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setImgIdx(i)}
                            className={cn(
                              "h-1.5 rounded-full transition-all",
                              i === imgIdx
                                ? "w-5 bg-white"
                                : "w-1.5 bg-white/50",
                            )}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  <div className="absolute top-3 left-3">
                    <span
                      className={cn(
                        "fnb-tag text-[11px] font-semibold",
                        item.approvalStatus === "approved"
                          ? "fnb-tag-green"
                          : "fnb-tag-brand",
                      )}
                    >
                      {item.approvalStatus === "approved"
                        ? "✓ Đã duyệt"
                        : "● Chờ duyệt"}
                    </span>
                  </div>
                </>
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-8xl opacity-20"
                  style={{ background: "var(--brand-pale)" }}
                >
                  🔍
                </div>
              )}
            </div>
            {/* Thumbnail strip */}
            {imgs.length > 1 && (
              <div
                className="flex gap-2 p-3 overflow-x-auto border-t"
                style={{ borderColor: "var(--border-c)" }}
              >
                {imgs.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={cn(
                      "shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all",
                      i === imgIdx
                        ? "border-orange-500"
                        : "border-transparent opacity-60 hover:opacity-100",
                    )}
                  >
                    <img
                      src={img.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Title + Requirements Summary */}
          <Card>
            <CardContent className="pt-6">
              <h1
                className="font-display font-bold text-2xl mb-4"
                style={{ color: "var(--text-1)" }}
              >
                {title}
              </h1>

              {/* Budget block */}
              <div
                className="rounded-xl p-4 mb-4 flex flex-wrap gap-6"
                style={{
                  background: "var(--brand-pale)",
                  border: "1px solid rgba(249,115,22,0.2)",
                }}
              >
                <div>
                  <p
                    className="text-xs font-medium mb-1"
                    style={{ color: "var(--text-3)" }}
                  >
                    Ngân sách đầu tư
                  </p>
                  <p
                    className="font-display font-black text-xl"
                    style={{ color: "var(--brand)" }}
                  >
                    {formatPriceExact(String(item.budgetMin))} - {formatPriceExact(String(item.budgetMax))} {item.currencyUnit}
                  </p>
                </div>
              </div>

              <div
                className="flex items-center gap-1.5 text-sm"
                style={{ color: "var(--text-3)" }}
              >
                <MapPin size={14} /> Khu vực mong muốn: {fullAddr || "Chưa xác định"}
              </div>
            </CardContent>
          </Card>

          {/* Detailed Requirements */}
          <Card>
            <CardHeader>
              <CardTitle>Yêu cầu chi tiết</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                {[
                  {
                    icon: <Layers size={15} />,
                    label: "Diện tích",
                    value: `${item.preferredAreaMin} - ${item.preferredAreaMax} ${item.areaUnit}`,
                  },
                  {
                    icon: <Utensils size={15} />,
                    label: "Sức chứa",
                    value: item.seatingCapacity ? `${item.seatingCapacity} chỗ` : "Không yêu cầu",
                  },
                  {
                    icon: <MapPin size={15} />,
                    label: "Cách trung tâm",
                    value: item.distanceToCityCenter ? `${item.distanceToCityCenter} km` : "Không yêu cầu",
                  },
                ].map(({ icon, label, value }) => (
                  <div key={label} className="flex items-start gap-2.5">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{
                        background: "var(--brand-dim)",
                        color: "var(--brand)",
                      }}
                    >
                      {icon}
                    </div>
                    <div>
                      <p
                        className="text-[11px] font-medium"
                        style={{ color: "var(--text-3)" }}
                      >
                        {label}
                      </p>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "var(--text-1)" }}
                      >
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4 pt-4 border-t" style={{ borderColor: "var(--border-c)" }}>
                <div>
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-1" style={{ color: "var(--text-1)" }}>
                    <Target size={16} className="text-orange-500" /> Loại hình kinh doanh mong muốn
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {item.preferredCategories?.map((cat, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-lg">
                        {cat}
                      </span>
                    ))}
                    {(!item.preferredCategories || item.preferredCategories.length === 0) && (
                      <span className="text-sm text-gray-500">Không có yêu cầu cụ thể</span>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-sm mb-2" style={{ color: "var(--text-1)" }}>
                    Yêu cầu thêm
                  </h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {item.additionalRequirements || "Không có yêu cầu thêm."}
                  </p>
                </div>
              </div>

            </CardContent>
          </Card>
        </div>

        {/* ── Right: Sidebar ── */}
        <div className="space-y-4">
          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle>Người có nhu cầu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "var(--brand-dim)" }}
                >
                  <UserIcon size={16} style={{ color: "var(--brand)" }} />
                </div>
                <div>
                  <p
                    className="font-semibold text-sm"
                    style={{ color: "var(--text-1)" }}
                  >
                    {item.creator?.username}
                  </p>
                  <p className="text-xs" style={{ color: "var(--text-3)" }}>
                    {item.creator?.email}
                  </p>
                </div>
              </div>

              {(!user || user.id !== item.createdBy) && (
                user ? (
                  <>
                    {needsToSignNda ? (
                      <button
                        onClick={() => setNdaOpen(true)}
                        className="w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all btn-brand"
                      >
                        Ký NDA để Gửi thông tin
                      </button>
                    ) : (
                      <button
                        onClick={() => setOfferOpen(true)}
                        disabled={isOffered}
                        className={cn(
                          "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all",
                          isOffered
                            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "btn-brand"
                        )}
                      >
                        {isOffered ? "Đã gửi thông tin" : "Gửi thông tin cho sang nhượng"}
                      </button>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="btn-brand w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
                  >
                    Đăng nhập để gửi thông tin
                  </Link>
                )
              )}

              {user?.id !== item.createdBy && (
                <button
                  onClick={() => toggleSave(item.id, "BusinessTransferRequest")}
                  className={cn(
                    "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 border transition-all mt-2",
                    saved
                      ? "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                >
                  <Heart size={14} className={cn(saved && "fill-orange-500 text-orange-500")} />
                  {saved ? "Đã lưu tin" : "Lưu tin"}
                </button>
              )}
            </CardContent>
          </Card>

          {/* Meta info */}
          <Card>
            <CardHeader>
              <CardTitle>Chi tiết</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                {[
                  ["Tỉnh/TP", item.province?.name],
                  ["Quận/Huyện", item.district?.name],
                  ["Phường/Xã", item.ward?.name],
                  ["Quốc gia", item.country?.name],
                  [
                    "Trạng thái",
                    item.approvalStatus === "approved"
                      ? "✓ Đã duyệt"
                      : "⏳ Chờ duyệt",
                  ],
                  [
                    "Ngày đăng",
                    new Date(item.createdAt).toLocaleDateString("vi-VN"),
                  ],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between items-start py-2 border-b last:border-0 gap-2"
                    style={{ borderColor: "var(--border-c)" }}
                  >
                    <span
                      className="text-xs shrink-0"
                      style={{ color: "var(--text-3)" }}
                    >
                      {k}
                    </span>
                    <span
                      className="text-xs font-medium text-right"
                      style={{ color: "var(--text-1)" }}
                    >
                      {v ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ══ OFFER MODAL ══ */}
      <OfferModal
        isOpen={offerOpen}
        onClose={() => setOfferOpen(false)}
        title="Gửi thông tin cho sang nhượng"
        onSubmit={handleSubmit(onSubmitOffer)}
        isPending={isPending}
        submitText="Gửi thông tin"
      >
        <Textarea
          label="Lời nhắn *"
          placeholder="Giới thiệu về cơ sở sang nhượng của bạn..."
          rows={4}
          error={errors.message?.message}
          {...register("message")}
        />
      </OfferModal>
      {/* ══ NDA MODAL ══ */}
      <NdaSignModal
        isOpen={ndaOpen}
        onClose={() => setNdaOpen(false)}
        entityType="BusinessTransferRequest"
        entityId={item.id}
        description="Bạn cần ký Thỏa thuận bảo mật thông tin (NDA) để trao đổi với người đăng."
        onSuccess={() => {
          setNdaOpen(false);
          setOfferOpen(true);
        }}
      />
    </div>
  );
}
